import React from 'react'
import { createBrowserRouter, Navigate } from 'react-router-dom'
import App from '../App'
import AuthLayout from '../layouts/auth-layout'
import AuthNoLayout from '../layouts/auth-no-layout'
import ConsumerPrimaryLayout from '../layouts/consumer/primary-layout'
import MaintenancePage from '../pages/maintenance'
import NotFoundPage from '../pages/not-found'
import { routes } from './routes'

export const consumerRouter = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      {
        element: <ConsumerPrimaryLayout />,
        children: [
          {
            index: true,
            element: <Navigate to='/home' />
          },
          {
            path: '/index.html',
            element: <Navigate to='/home' />
          },
          {
            path: routes.author.path,
            lazy: () => import('../pages/author')
          },
          {
            path: routes.article.path,
            lazy: () => import('../pages/location')
          },
          {
            path: routes.home.path,
            lazy: () => import('../pages/home')
          },
          {
            path: routes.bookmarks.path,
            lazy: () => import('../pages/bookmarks')
          },
          {
            path: routes.myLearning.path,
            lazy: () => import('../pages/my-learning')
          },
          {
            path: routes.reporting.path,
            lazy: () => import('../pages/reporting')
          },
          {
            path: routes.serp.path,
            lazy: () => import('../pages/search-results')
          },
          {
            // Account settings is one page: `pages/profile` reads `:section`
            // and renders that section, or every section on one page below
            // `md`. The paths it answers to are the `profile-*` entries in
            // `routes`. Retention below stays a static route, which React
            // Router ranks above `:section`.
            path: `${routes.profile.path}/:section?`,
            lazy: () => import('../pages/profile')
          },
          {
            path: routes.retention.path,
            lazy: () => import('../pages/profile/components/subscription-and-billing/components/retention')
          },
          {
            path: routes.playlists.path,
            lazy: () => import('../pages/playlists')
          },
          {
            path: routes.quizzes.path,
            lazy: () => import('../pages/quizzes')
          },
          {
            path: routes.playlist.path,
            lazy: async () => {
              const { PlaylistViewPage } = await import('../pages/playlist-detail')
              return {
                Component: PlaylistViewPage
              }
            }
          },
          {
            path: routes['playlist-edit'].path,
            lazy: async () => {
              const { PlaylistEditPage } = await import('../pages/playlist-detail')
              return {
                Component: PlaylistEditPage
              }
            }
          },
          {
            path: routes['playlist-create'].path,
            lazy: async () => {
              const { PlaylistCreatePage } = await import('../pages/playlist-detail')
              return {
                Component: PlaylistCreatePage
              }
            }
          },
          {
            path: routes['skill-bites'].path,
            lazy: () => import('../pages/skill-bites')
          },
          {
            path: routes['skill-bite'].path,
            lazy: () => import('../pages/skill-bite-detail')
          },
          {
            path: routes['skill-bite-session'].path,
            lazy: () => import('../pages/skill-bite-session')
          },
          {
            path: routes['a11y-statement'].path,
            lazy: () => import('../pages/policies/a11y-statement')
          },
          {
            path: routes['about-our-content'].path,
            lazy: () => import('../pages/policies/about-our-content')
          },
          {
            path: routes['whats-new'].path,
            lazy: () => import('../pages/whats-new')
          },
          {
            path: routes['whats-new-item'].path,
            lazy: () => import('../pages/whats-new/item')
          },
          {
            path: routes['newest-releases'].path,
            lazy: () => import('../pages/newest-releases')
          },
          {
            path: routes['most-popular'].path,
            lazy: () => import('../pages/most-popular')
          },
          // {
          //   path: routes['bonus-material'].path,
          //   lazy: () => import('../pages/bonus-material')
          // },
          {
            path: routes.welcome.path,
            lazy: () => import('../pages/welcome')
          }
        ]
      },
      {
        element: <AuthLayout />,
        children: [
          {
            path: routes.login.path,
            lazy: () => import('../pages/sign-in')
          },
          {
            path: routes.register.path,
            lazy: () => import('../pages/sign-up')
          },
          {
            path: routes['recovery-password'].path,
            lazy: () => import('../pages/password-recovery')
          }
        ]
      },
      {
        element: <AuthNoLayout />,
        children: [
          {
            path: routes.b2cRegister.path,
            lazy: () => import('../pages/b2c-signup')
          },
          {
            path: routes.b2cSocialSignUpRedirect.path,
            lazy: () => import('../pages/b2c-social-redirect')
          },
          {
            path: routes.b2cRegisterSuccess.path,
            lazy: () => import('../pages/b2c-signup-success')
          },
          {
            path: routes.b2cFreeRegisterSuccess.path,
            lazy: async () => {
              const { B2CSignupSuccessFree } = await import('../pages/b2c-signup-success')
              return {
                Component: B2CSignupSuccessFree
              }
            }
          },
          {
            path: routes['verify-email'].path,
            lazy: () => import('../pages/verify-email')
          },
          {
            path: routes['resend-email'].path,
            lazy: () => import('../pages/resend-email')
          },
          {
            path: routes['recovered-password'].path,
            lazy: () => import('../pages/password-recovered')
          },
          {
            path: routes['reset-password-end'].path,
            lazy: () => import('../pages/password-reset-end')
          },
          {
            path: routes['reset-password'].path,
            lazy: () => import('../pages/password-reset')
          },
          {
            path: routes['change-password'].path,
            lazy: () => import('../pages/password-force-change')
          },
          {
            path: routes.maintenance.path,
            element: <MaintenancePage />
          },
          {
            path: routes.signin.path,
            lazy: () => import('../pages/sign-in-completion')
          },
          {
            path: routes.signout.path,
            lazy: () => import('../pages/sign-out-completion')
          },
          {
            path: routes.internalLogin.path,
            lazy: () => import('../pages/admin-internal-login')
          }
        ]
      },
      {
        path: routes.error404.path,
        element: <NotFoundPage />
      },
      {
        path: routes.migrationError404.path,
        element: <NotFoundPage title='This page is no longer available' desc={["The content you're looking for has been retired.", 'Go to the homepage to discover more great resources.']} />
      },
      {
        path: '*',
        element: <NotFoundPage />
      }
    ]
  }
])
